import React, { useLayoutEffect, useRef, useState } from 'react';

import { appConfig } from '../../config/runtimeConfig';
import LoadingCover from '../common/LoadingCover';

const widthScaleSmall = 2;

interface OwnProps {
  src: string;
  alt: string;
  notCDN?: boolean;
  loading?: 'eager' | 'lazy' | undefined;
  className?: string;
  objectFit?: 'contain' | 'cover' | 'fill' | 'none' | 'scale-down';
  maxWidth?: number;
}

const ImageLoader: React.FC<OwnProps> = (props) => {
  const {
    src,
    notCDN = false,
    alt,
    loading,
    objectFit = 'contain',
    maxWidth,
  } = props;

  const aspectRef = useRef(new Image());
  const bigImageRef = useRef(new Image());

  const [url, setUrl] = useState('');
  const [fullUrl, setFullUrl] = useState('');
  const isLoading = fullUrl && url !== fullUrl;
  const [customClass, setCustomClass] = useState('loading');

  useLayoutEffect(() => {
    let isMounted = true;
    const getTargetWidth = (clientWidth: number): number => {
      const sizes = {
        xsmall: 150,
        small: 300,
        medium: 600,
        large: 1200,
        xlarge: 2400,
      };
      switch (true) {
        case clientWidth >= sizes.large:
          return sizes.xlarge;
        case clientWidth >= sizes.medium:
          return sizes.large;
        case clientWidth >= sizes.small:
          return sizes.medium;
        case clientWidth >= sizes.xsmall:
          return sizes.small;
        default:
          return sizes.xsmall;
      }
    };

    const setImageBig = () => {
      if (isMounted && bigImageRef.current) {
        setCustomClass('loaded');
        setUrl(bigImageRef.current.src);
      }
    };

    // Create a new Image object for each effect run
    const bigImage = new Image();
    bigImageRef.current = bigImage;

    setCustomClass('loading');
    const avRef = aspectRef.current;
    if (avRef) {
      const clientWidth =
        avRef.clientWidth ||
        avRef.parentElement?.clientWidth ||
        (typeof window !== 'undefined' ? window.innerWidth : 0);
      const targetWidth = maxWidth
        ? Math.min(getTargetWidth(clientWidth), maxWidth)
        : getTargetWidth(clientWidth);
      const bigUrl = `${appConfig.APP_CDN_BASE_URL}/${targetWidth}x0/filters:format(webp)/${appConfig.APP_STAGE}/${src}`;
      const fullUrlSmall = `${appConfig.APP_CDN_BASE_URL}/${widthScaleSmall}x0/filters:format(webp)/${appConfig.APP_STAGE}/${src}`;
      // Set the onload handler on our local reference
      bigImage.onload = setImageBig;

      bigImage.src = bigUrl;
      setUrl(fullUrlSmall);
      setFullUrl(bigUrl);
    }

    if (!src || src === '') {
      setUrl('');
    }

    return () => {
      isMounted = false;
      if (bigImage) {
        bigImage.src = '';
      }
    };
  }, [src, maxWidth]);

  if (!src || src === '' || url === '' || notCDN) {
    return (
      <img
        className={`image ${customClass}`}
        id="image"
        loading={loading}
        alt={alt}
        src={undefined}
        ref={aspectRef}
        style={{ width: '100%' }}
      />
    );
  }

  return (
    <React.Fragment>
      {isLoading && <LoadingCover />}
      <img
        className={`image ${customClass}`}
        id="image"
        loading={loading}
        alt={alt}
        src={url}
        ref={aspectRef}
        style={{
          width: '100%',
          height: '100%',
          objectFit: objectFit,
        }}
      />
    </React.Fragment>
  );
};

export default ImageLoader;
